import clsx from "clsx";
import { CircleAlert, CircleCheck, CircleHelp, RotateCcw } from "lucide-react";
import * as React from "react";

import DataTable, { type DataTableColumn } from "../DataTable/DataTable";
import Tooltip from "../Tooltip/Tooltip";
import styles from "./CsvTable.module.scss";

export interface CsvTableProps {
  headers: readonly string[];
  rows: readonly (readonly string[])[];
  ariaLabel?: string;
  className?: string;
  /** Why each row failed, by row index. Adds a first column with an icon that shows a row's reason on hover. */
  rowErrors?: readonly (string | undefined)[];
  /** Explicit processing outcomes; absent outcomes are never assumed successful. */
  onRecoveryClick?: (rowIndex: number) => void;
  rowStatuses?: readonly ("succeeded" | "recovered" | "failed" | "unattempted" | "unknown" | undefined)[];
  /** Clicking a row selects it, highlighting it as the standard DataTable does. */
  selectableRows?: boolean;
  /** Called with a row's index when it is clicked. */
  onRowClick?: (rowIndex: number) => void;
}

type CsvTableRow = {
  id: number;
  [field: string]: string | number;
};

const getColumnWidth = (header: string): number => Math.min(320, Math.max(160, header.length * 9 + 48));

const ROW_ERROR_FIELD = "row-error";

const CsvTable = ({ headers, rows, ariaLabel = "CSV data", className, rowErrors, rowStatuses, onRecoveryClick, selectableRows = false, onRowClick }: CsvTableProps) => {
  const hasRowErrors = Boolean(rowErrors?.some(Boolean));
  const hasRowStatuses = Boolean(rowStatuses?.some(Boolean));
  const columns = React.useMemo<DataTableColumn[]>(
    () => [
      ...(hasRowErrors || hasRowStatuses
        ? [{
            field: ROW_ERROR_FIELD,
            headerName: hasRowStatuses ? "Status" : "Error",
            width: hasRowStatuses ? 88 : 72,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            align: "center",
            headerAlign: "center",
            renderCell: ({ value, row }) => {
              const status = row["row-status"];
              if (status) {
                const label = status === "recovered" ? "Recovered" : status === "succeeded" ? "Succeeded" : status === "unattempted" ? "Not attempted" : status === "failed" ? "Failed" : "Outcome unknown";
                const StatusIcon = status === "recovered" ? RotateCcw : status === "succeeded" ? CircleCheck : (status === "unattempted" || status === "failed") ? CircleAlert : CircleHelp;
                const reason = String(value ?? "");
                const description = reason ? (reason.toLowerCase().startsWith(`${label.toLowerCase()}:`) ? reason : `${label}: ${reason}`) : label;
                const indicator = <span className={status === "failed" || status === "unattempted" ? styles.rowError : styles.rowStatus} role="img" aria-label={description}>
                    <StatusIcon size={16} />
                  </span>;
                return <Tooltip title={description} asChild>{status !== "succeeded" && (onRowClick || onRecoveryClick) ? (
                  <button type="button" className={styles.statusButton} aria-label={`View row ${Number(row.id) + 2}: ${description}`}
                    onClick={event => { event.stopPropagation(); if (status === "recovered" && onRecoveryClick) onRecoveryClick(Number(row.id)); else onRowClick?.(Number(row.id)); }}>{indicator}</button>
                ) : indicator}</Tooltip>;
              }
              return (value ? (
              <Tooltip title={String(value)}>
                <span className={styles.rowError} role="img" aria-label={String(value)}>
                  <CircleAlert size={16} />
                </span>
              </Tooltip>
            ) : null);
            },
          } satisfies DataTableColumn]
        : []),
      ...headers.map((header, columnIndex): DataTableColumn => ({
        field: `column-${columnIndex}`,
        headerName: header || `Column ${columnIndex + 1}`,
        minWidth: 160,
        width: getColumnWidth(header),
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ value }) => <span className={styles.cellValue}>{String(value ?? "")}</span>,
      })),
    ],
    [headers, hasRowErrors, hasRowStatuses, onRowClick, onRecoveryClick],
  );
  const tableRows = React.useMemo<CsvTableRow[]>(
    () =>
      rows.map((row, rowIndex) => {
        const tableRow: CsvTableRow = { id: rowIndex };
        headers.forEach((_, columnIndex) => {
          tableRow[`column-${columnIndex}`] = row[columnIndex] ?? "";
        });
        if (hasRowErrors || hasRowStatuses) tableRow[ROW_ERROR_FIELD] = rowErrors?.[rowIndex] ?? "";
        if (hasRowStatuses) tableRow["row-status"] = rowStatuses?.[rowIndex] ?? "";
        return tableRow;
      }),
    [headers, rows, rowErrors, rowStatuses, hasRowErrors, hasRowStatuses],
  );

  return (
    <div
      className={clsx(styles.root, className)}
      data-deci-csv-table="true"
    >
      <DataTable
        aria-label={ariaLabel}
        columns={columns}
        rows={tableRows}
        getRowHeight={() => "auto"}
        rowSelection={selectableRows}
        disableRowSelectionOnClick={!selectableRows}
        onRowClick={onRowClick ? (params) => onRowClick(Number(params.id)) : undefined}
        hideFooter
      />
    </div>
  );
};

export default CsvTable;
